import type { SummaryResponse } from "./api-types";

/**
 * Inner HTML for all customer sections (used by the dashboard view).
 */
export function buildCustomerSectionsHtml(
  customers: string[],
  summaries: Map<string, SummaryResponse | Error>,
  escapeHtml: (s: string) => string,
  secondsToHhMm: (sec: number) => string,
): string {
  return customers
    .map((c) =>
      buildOneCustomerSectionHtml(c, summaries.get(c), escapeHtml, secondsToHhMm),
    )
    .join("");
}

function buildOneCustomerSectionHtml(
  customer: string,
  data: SummaryResponse | Error | undefined,
  escapeHtml: (s: string) => string,
  secondsToHhMm: (sec: number) => string,
): string {
  if (data instanceof Error) {
    return `<section class="customer"><h2>${escapeHtml(customer)}</h2><p class="error">${escapeHtml(data.message)}</p></section>`;
  }
  if (!data) return "";
  const byEnv = Array.isArray(data.by_environment) ? data.by_environment : [];
  const instances = Array.isArray(data.instances) ? data.instances : [];
  const envRows = byEnv
    .map(
      (e) =>
        `<tr><td>${e.environment == null ? "— (untagged)" : escapeHtml(e.environment)}</td><td>${secondsToHhMm(e.lifetime_seconds)}</td><td>${secondsToHhMm(e.month_seconds)}</td></tr>`,
    )
    .join("");
  const instRows = instances
    .map((i) => {
      const thisRun =
        (i.current_run_seconds ?? 0) > 0
          ? secondsToHhMm(i.current_run_seconds ?? 0)
          : "—";
      return `<tr><td><code>${escapeHtml(i.instance_id)}</code></td><td>${escapeHtml(i.region)}</td><td>${i.environment == null ? "—" : escapeHtml(i.environment)}</td><td>${thisRun}</td><td>${secondsToHhMm(i.lifetime_seconds)}</td><td>${secondsToHhMm(i.month_seconds)}</td></tr>`;
    })
    .join("");
  return `<section class="customer">
        <h2>${escapeHtml(customer)}</h2>
        <p class="muted">As of ${escapeHtml(data.as_of)} · Month <strong>${escapeHtml(data.month)}</strong></p>
        <div class="totals">
          <div><span class="label">Grand total (lifetime)</span> <strong>${secondsToHhMm(data.lifetime_seconds)}</strong></div>
          <div><span class="label">Month total (${escapeHtml(data.month)})</span> <strong>${secondsToHhMm(data.month_seconds)}</strong></div>
        </div>
        <h3>By environment</h3>
        <table><thead><tr><th>Environment</th><th>Lifetime</th><th>Month</th></tr></thead><tbody>${envRows || "<tr><td colspan=\"3\">None</td></tr>"}</tbody></table>
        <h3>By instance</h3>
        <table><thead><tr><th>Instance</th><th>Region</th><th>Environment</th><th>This run</th><th>Lifetime</th><th>Month</th></tr></thead><tbody>${instRows || "<tr><td colspan=\"6\">No instances</td></tr>"}</tbody></table>
        <p class="note muted">This run: wall time since the instance’s current start (OPEN). Cleared when the instance stops (no OPEN row).</p>
      </section>`;
}

export function buildDashboardPageHtml(
  userName: string,
  month: string,
  customerSectionsHtml: string,
  escapeHtml: (s: string) => string,
): string {
  return `
    <header class="top">
      <div><h1>EC2 usage</h1><p class="muted">${escapeHtml(userName)}</p></div>
      <div class="controls">
        <label>Month (UTC) <input type="month" id="month" value="${escapeHtml(month)}" /></label>
        <button type="button" id="reload" class="primary">Refresh</button>
        <button type="button" id="logout">Sign out</button>
      </div>
    </header>
    <p class="note muted">Times are H:MM (hours and minutes). Values truncate to whole minutes before formatting; rounding policy TBD.</p>
    ${customerSectionsHtml}
  `;
}
